import {
	Component,
	computed,
	effect,
	inject,
	input,
	OnDestroy,
	model,
	output,
	signal,
	untracked,
	viewChild,
	ViewEncapsulation,
} from '@angular/core';
import { FormValueControl } from '@angular/forms/signals';
import Quill from 'quill';
import type Op from 'quill-delta/dist/Op';
import { QuillModule } from 'ngx-quill';
import 'quill-mention/autoregister';
import { VehiclesSelectService } from '../../vehicles-select/vehicles-select.service';
import { Meta, Response, Union, UnionService, Vehicle } from '@agilox/common';
import {
	ChatMessage,
	ContextMetadata,
	MentionChannel,
	MentionValue,
	UnionMetadata,
	VehicleMetadata,
} from '../models';
import {
	ButtonComponent,
	DropdownDirective,
	DropdownModule,
	IconModule,
	TagComponent,
	TooltipModule,
} from '@agilox/ui';
import { TranslatePipe } from '@ngx-translate/core';
import { AIChatContextService } from '../ai-chat-context.service';

interface ContextItemEntry {
	mentionChannel: string;
	description: string;
	sourceItem: any;
}

interface ContextItemResult {
	sourceItem: any;
	description: string;
}

interface MentionOccurrence {
	start: number;
	end: number;
	mention: MentionReplacement['mention'];
}

interface MentionReplacement {
	search: string;
	mention: { id: string; value: string; denotationChar: string };
	setup: () => void;
}

interface ExtractedMentions {
	mentions: VehicleMetadata[];
	unions: UnionMetadata[];
	contextItems: ContextMetadata[];
}

@Component({
	selector: 'ai-chat-input',
	templateUrl: './ai-chat-input.component.html',
	styleUrl: './ai-chat-input.component.scss',
	encapsulation: ViewEncapsulation.None,
	imports: [
		QuillModule,
		IconModule,
		TranslatePipe,
		TooltipModule,
		ButtonComponent,
		DropdownModule,
		TagComponent,
	],
})
export class AIChatInput implements FormValueControl<MentionValue | null>, OnDestroy {
	private vehiclesSelectService = inject(VehiclesSelectService);
	private unionService = inject(UnionService);
	private aIChatContextService = inject(AIChatContextService);

	placeholder = input<string>('');
	isSending = input(false);
	invalid = input<boolean>(false);
	touched = input<boolean>(false);
	disabled = input<boolean>(false);

	value = model<MentionValue | null>(null);

	sendMessage = output<void>();
	cancelMessage = output<void>();
	touch = output<void>();

	dropdown = viewChild<DropdownDirective>(DropdownDirective);

	allContextGroups = computed(() => {
		const vehicleGroup = {
			icon: 'robot_one',
			title: 'ai_chat.add_vehicle',
			mentionChannel: MentionChannel.Vehicle,
		};

		const unionGroup = {
			icon: 'fleet',
			title: 'ai_chat.add_union',
			mentionChannel: MentionChannel.Union,
		};

		const extraGroups = this.extraGroups().map((group) => ({
			icon: group.icon,
			title: group.tooltipText || group.title,
			mentionChannel: group.mentionChannel,
		}));
		return [vehicleGroup, unionGroup, ...extraGroups];
	});

	quillConfig = computed(() => {
		const extra = this.aIChatContextService.buildExtraMentionConfig();
		const chars = [MentionChannel.Vehicle, MentionChannel.Union, ...extra.mentionDenotationChars];

		return {
			modules: {
				toolbar: false,
				keyboard: {
					bindings: {
						shiftEnter: { key: 'Enter', shiftKey: true, handler: () => true },
						enter: {
							key: 'Enter',
							handler: () => {
								this.sendMessage.emit();
								return false;
							},
						},
						// Cmd+Enter (Mac) / Ctrl+Enter (Windows) also sends.
						sendEnter: {
							key: 'Enter',
							shortKey: true,
							handler: () => {
								this.sendMessage.emit();
								return false;
							},
						},
					},
				},
				mention: {
					allowedChars: /^[A-Za-z0-9_\-\s]*$/,
					mentionDenotationChars: chars,
					mentionContainerClass: 'mention-container',
					mentionListClass: 'mention-list',
					listItemClass: 'mention-item',
					positioningStrategy: 'fixed',
					source: this.buildMentionSource(extra),
					renderItem: this.buildRenderItem(extra),
					onSelect: (item: any, insertItem: Function) => {
						this.handleMentionSelect(item, insertItem);
					},
				},
			},
			formats: ['mention'],
			placeholder: this.placeholder(),
		};
	});

	public extraGroups = this.aIChatContextService.groups;

	focused = signal(false);
	empty = signal(true);

	public quillEditor: Quill | undefined;

	private mentionsMap = new Map<string, Vehicle>();
	private unionsMap = new Map<string, Union>();
	private contextItemsMap = new Map<string, ContextItemEntry>();
	private mutationObservers: MutationObserver[] = [];
	private pendingRestore: ChatMessage | null = null;
	private pendingDisabled: boolean | null = null;
	private lastEmitted = '';

	constructor() {
		effect(() => {
			const groups = this.extraGroups();
			if (this.quillEditor) {
				this.updateMentionModule();
			}
		});

		effect(() => {
			const value = this.value();
			untracked(() => this.showValue(value));
		});

		effect(() => {
			const disabled = this.disabled();
			untracked(() => this.setDisabled(disabled));
		});
	}

	private buildMentionSource(extra: ReturnType<AIChatContextService['buildExtraMentionConfig']>) {
		return (searchTerm: string, renderList: Function, denotationChar: string) => {
			if (denotationChar === MentionChannel.Vehicle) {
				this.fetchVehicles(searchTerm, renderList);
			} else if (denotationChar === MentionChannel.Union) {
				this.fetchUnions(searchTerm, renderList);
			} else {
				extra.source(searchTerm, renderList, denotationChar);
			}
		};
	}

	private buildRenderItem(extra: ReturnType<AIChatContextService['buildExtraMentionConfig']>) {
		return (item: any, _searchTerm?: string) => {
			if ((item && item.denotationChar === MentionChannel.Vehicle) || (item && 'serial' in item)) {
				return `${item.value}${item.serial ? ` (${item.serial})` : ''}`;
			}
			if ((item && item.denotationChar === MentionChannel.Union) || (item && 'union_id' in item)) {
				return `${item.value}${item.company ? ` (${item.company})` : ''}`;
			}
			return extra.renderItem(item);
		};
	}

	private updateMentionModule(): void {
		if (!this.quillEditor) return;

		const mentionModule = this.quillEditor.getModule('mention') as any;
		if (!mentionModule) return;

		const extra = this.aIChatContextService.buildExtraMentionConfig();
		mentionModule.options.mentionDenotationChars = [
			MentionChannel.Vehicle,
			MentionChannel.Union,
			...extra.mentionDenotationChars,
		];
		mentionModule.options.source = this.buildMentionSource(extra);
		mentionModule.options.renderItem = this.buildRenderItem(extra);
		mentionModule.options.onSelect = (item: any, insertItem: Function) => {
			this.handleMentionSelect(item, insertItem);
		};
	}

	private fetchVehicles(searchTerm: string, renderList: Function): void {
		const page: Meta = { number: 0, size: 50, total: 0 };

		this.vehiclesSelectService.fetchVehicles(searchTerm, page).subscribe({
			next: (response: Response<Vehicle>) => {
				const items = response.data.map((vehicle) => {
					this.mentionsMap.set(vehicle.uuid, vehicle);

					return {
						id: vehicle.uuid,
						value: vehicle.name,
						serial: vehicle.serial,
						union: vehicle.union,
						denotationChar: MentionChannel.Vehicle,
					};
				});

				renderList(items, searchTerm);
			},
			error: () => {
				renderList([], searchTerm);
			},
		});
	}

	private fetchUnions(searchTerm: string, renderList: Function): void {
		this.unionService.fetchUnions().subscribe({
			next: (unions: Union[]) => {
				const filtered = unions.filter((union) => {
					const lowerSearch = searchTerm.toLowerCase();
					return (
						union.union_name.toLowerCase().includes(lowerSearch) ||
						union.company_name.toLowerCase().includes(lowerSearch)
					);
				});

				const items = filtered.map((union) => {
					this.unionsMap.set(union.union_id, union);

					return {
						id: union.union_id,
						value: union.union_name,
						company: union.company_name,
						denotationChar: MentionChannel.Union,
					};
				});

				renderList(items, searchTerm);
			},
			error: () => {
				renderList([], searchTerm);
			},
		});
	}

	private handleMentionSelect(item: any, insertItem: Function): void {
		const vehicle = item?.id ? this.mentionsMap.get(item.id) : undefined;
		const union = item?.id ? this.unionsMap.get(item.id) : undefined;

		if (vehicle) {
			insertItem({ ...item, value: `${vehicle.name} (${vehicle.serial})` });
		} else if (union) {
			insertItem({ ...item, value: `${union.union_name} (${union.company_name})` });
		} else {
			if (
				item?.id &&
				item?.denotationChar &&
				item.denotationChar !== MentionChannel.Vehicle &&
				item.denotationChar !== MentionChannel.Union
			) {
				const { sourceItem, description } = this.findContextItemWithDescription(
					item.id,
					item.denotationChar
				);

				if (sourceItem) {
					this.contextItemsMap.set(item.id, {
						mentionChannel: item.denotationChar,
						description: description || '',
						sourceItem: sourceItem,
					});
				}
			}
			insertItem(item);
		}
		this.notifyChange();
	}

	private findContextItemWithDescription(id: string, denotationChar: string): ContextItemResult {
		const groups = this.aIChatContextService.groups();
		const group = groups.find((g) => g.mentionChannel === denotationChar);
		if (!group) return { sourceItem: undefined, description: '' };

		const sourceItem = group.items.find((it) => it.searchValue === id);
		return { sourceItem, description: group.description };
	}

	private extractMentionsFromContent(): ExtractedMentions {
		if (!this.quillEditor) return { mentions: [], unions: [], contextItems: [] };

		const result: ExtractedMentions = { mentions: [], unions: [], contextItems: [] };
		const delta = this.quillEditor.getContents();

		delta.ops?.forEach((op) => {
			if (typeof op.insert === 'object' && op.insert !== null && 'mention' in op.insert) {
				this.classifyMention((op.insert as any).mention, result);
			}
		});

		return result;
	}

	private classifyMention(mentionData: any, result: ExtractedMentions): void {
		const vehicle = this.mentionsMap.get(mentionData.id);
		if (vehicle) {
			result.mentions.push({
				uuid: vehicle.uuid,
				name: vehicle.name,
				serial: vehicle.serial,
				union: vehicle.union,
			});
			return;
		}

		const union = this.unionsMap.get(mentionData.id);
		if (union) {
			result.unions.push({
				union_id: union.union_id,
				union_name: union.union_name,
				company_name: union.company_name,
			});
			return;
		}

		const contextItem = this.contextItemsMap.get(mentionData.id);
		if (contextItem) {
			result.contextItems.push({
				mentionChannel: contextItem.mentionChannel,
				description: contextItem.description,
				data: contextItem.sourceItem.toJson(),
			});
		}
	}

	public getValue(): MentionValue {
		if (!this.quillEditor) {
			return { text: '', mentions: [], unions: [], contextItems: [] };
		}

		let text = '';
		const delta = this.quillEditor.getContents();

		delta.ops?.forEach((op) => {
			if (typeof op.insert === 'string') {
				text += op.insert;
			} else if (typeof op.insert === 'object' && op.insert !== null && 'mention' in op.insert) {
				const mentionData = (op.insert as any).mention;
				text += mentionData.value;
			}
		});

		const { mentions, unions, contextItems } = this.extractMentionsFromContent();

		return { text: text.trim(), mentions, unions, contextItems };
	}

	private notifyChange(): void {
		const value = this.getValue();
		this.lastEmitted = value.text;
		this.value.set(value);
		this.touch.emit();
	}

	editorCreated(editor: Quill): void {
		this.quillEditor = editor;

		editor.on('text-change', () => {
			this.empty.set((editor.getLength() ?? 1) <= 1);
			this.notifyChange();
		});

		editor.on('selection-change', (range) => {
			if (range) {
				this.focused.set(true);
			} else {
				this.focused.set(false);
			}
		});

		this.setupMentionScrolling();

		if (this.pendingDisabled !== null) {
			editor.enable(!this.pendingDisabled);
			this.pendingDisabled = null;
		}

		if (this.pendingRestore) {
			this.restoreMessage(this.pendingRestore);
			this.pendingRestore = null;
		}
	}

	private setupMentionScrolling(): void {
		const bodyObserver = new MutationObserver(() => {
			const mentionList = document.querySelector('.mention-list');
			if (mentionList) {
				const selectedObserver = new MutationObserver(() => {
					const selectedItem = mentionList.querySelector('.mention-item.selected') as HTMLElement;
					if (selectedItem) {
						selectedItem.scrollIntoView({
							block: 'nearest',
							inline: 'nearest',
						});
					}
				});

				selectedObserver.observe(mentionList, {
					attributes: true,
					attributeFilter: ['class'],
					subtree: true,
					childList: true,
				});
				this.mutationObservers.push(selectedObserver);
			}
		});

		bodyObserver.observe(document.body, {
			childList: true,
			subtree: true,
		});
		this.mutationObservers.push(bodyObserver);
	}

	ngOnDestroy(): void {
		this.mutationObservers.forEach((observer) => observer.disconnect());
		this.mutationObservers = [];
	}

	/** The field writing in; the editor's own edits are echoed back, so only apply what it lacks. */
	private showValue(value: MentionValue | null): void {
		if (!this.quillEditor) return;

		const text = value?.text ?? '';
		if (text === this.lastEmitted) {
			return;
		}
		this.lastEmitted = text;

		if (!text) {
			this.quillEditor.setText('');
			this.mentionsMap.clear();
			this.unionsMap.clear();
			this.contextItemsMap.clear();
			return;
		}
		this.quillEditor.setText(text);
	}

	setDisabled(isDisabled: boolean): void {
		if (this.quillEditor) {
			this.quillEditor.enable(!isDisabled);
		} else {
			this.pendingDisabled = isDisabled;
		}
	}

	onFocusIn(): void {
		this.focused.set(true);
		if (this.quillEditor) {
			this.quillEditor.focus();
		}
	}

	onKeyDown(event: KeyboardEvent): void {
		if (event.key === 'Enter') {
			event.preventDefault();
		}
	}

	// A plain click on the toolbar row (not on a button) focuses the editor.
	onToolbarClick(event: MouseEvent): void {
		if (this.isInteractiveTarget(event.target)) return;
		this.onFocusIn();
	}

	private isInteractiveTarget(target: EventTarget | null): boolean {
		return (
			target instanceof HTMLElement && !!target.closest('button, a, ui-button, [role="button"]')
		);
	}

	addMentionFor(denotationChar: string) {
		if (this.quillEditor && this.quillEditor.getModule('mention')) {
			(<any>this.quillEditor.getModule('mention')).openMenu(denotationChar);
		}
		this.dropdown()?.closeDropdown();
	}

	restoreMessage(msg: ChatMessage): void {
		if (!this.quillEditor) {
			this.pendingRestore = msg;
			return;
		}

		this.mentionsMap.clear();
		this.unionsMap.clear();
		this.contextItemsMap.clear();

		const replacements = this.buildReplacements(msg);
		replacements.forEach((r) => r.setup());

		const ops = this.buildDeltaOps(msg.content, replacements);
		this.quillEditor.setContents({ ops } as any);
		const length = this.quillEditor.getLength();
		this.quillEditor.setSelection(length, 0);
		this.notifyChange();
	}

	private buildReplacements(msg: ChatMessage): MentionReplacement[] {
		const replacements: MentionReplacement[] = [];

		for (const m of msg.mentions ?? []) {
			const display = `${m.name} (${m.serial})`;
			replacements.push({
				search: display,
				mention: { id: m.uuid, value: display, denotationChar: MentionChannel.Vehicle },
				setup: () => this.mentionsMap.set(m.uuid, m as any),
			});
		}

		for (const u of msg.unions ?? []) {
			const display = `${u.union_name} (${u.company_name})`;
			replacements.push({
				search: display,
				mention: { id: u.union_id, value: display, denotationChar: MentionChannel.Union },
				setup: () => this.unionsMap.set(u.union_id, u as any),
			});
		}

		for (const c of msg.contextItems ?? []) {
			const label = this.getContextLabel(c);
			replacements.push({
				search: label,
				mention: { id: label, value: label, denotationChar: c.mentionChannel },
				setup: () =>
					this.contextItemsMap.set(label, {
						mentionChannel: c.mentionChannel,
						description: c.description,
						sourceItem: { toJson: () => c.data, searchValue: label },
					}),
			});
		}

		return replacements;
	}

	private buildDeltaOps(text: string, replacements: MentionReplacement[]): Op[] {
		const occurrences: MentionOccurrence[] = [];
		for (const r of replacements) {
			let searchFrom = 0;
			while (searchFrom < text.length) {
				const idx = text.indexOf(r.search, searchFrom);
				if (idx === -1) break;
				occurrences.push({ start: idx, end: idx + r.search.length, mention: r.mention });
				searchFrom = idx + r.search.length;
			}
		}
		occurrences.sort((a, b) => a.start - b.start);

		const ops: Op[] = [];
		let cursor = 0;

		for (const occ of occurrences) {
			if (occ.start > cursor) {
				ops.push({ insert: text.slice(cursor, occ.start) });
			}
			ops.push({ insert: { mention: occ.mention } });
			cursor = occ.end;
		}

		if (cursor < text.length) {
			ops.push({ insert: text.slice(cursor) });
		}

		const lastInsert = ops[ops.length - 1]?.insert;
		if (!ops.length || typeof lastInsert !== 'string' || !lastInsert.endsWith('\n')) {
			ops.push({ insert: '\n' });
		}

		return ops;
	}

	private getContextLabel(c: ContextMetadata): string {
		const data = c.data as any;
		if (data?.headline) return `${c.mentionChannel} ${data.headline}`;
		if (data?.name) return `${c.mentionChannel} ${data.name}`;
		if (data?.title) return `${c.mentionChannel} ${data.title}`;
		return c.mentionChannel;
	}
}
